import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getModules, createModule, deleteModule, getTeachers, createSubject, deleteSubject, updateSubject, uploadDocument, deleteDocument, getDocumentUrl, formatApiError } from '../../lib/api';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { BookOpen, Plus, Trash2, Users, Loader2, ChevronRight, GraduationCap, Clock, Calendar, Pencil, Upload, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCourses() {
  const [modules, setModules] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: '', description: '', price_monthly: '', price_annual: '' });
  const [subjectForm, setSubjectForm] = useState({ title: '', description: '', teacher_id: '', duration_weeks: '4', classes_per_week: '1', order: '1' });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editSubject, setEditSubject] = useState(null);
  const [editForm, setEditForm] = useState({ teacher_id: '', duration_weeks: '', classes_per_week: '', order: '' });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [modulesData, teachersData] = await Promise.all([
        getModules(), getTeachers().catch(() => [])
      ]);
      setModules(modulesData);
      setTeachers(teachersData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateModule = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      await createModule({
        title: moduleForm.title, description: moduleForm.description,
        price_monthly: parseFloat(moduleForm.price_monthly), price_annual: parseFloat(moduleForm.price_annual)
      });
      toast.success('Módulo creado exitosamente');
      setShowModuleDialog(false);
      setModuleForm({ title: '', description: '', price_monthly: '', price_annual: '' });
      fetchData();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDeleteModule = async (id) => {
    if (!window.confirm('¿Eliminar este módulo?')) return;
    try { await deleteModule(id); toast.success('Módulo eliminado'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openSubjectDialog = (mod) => {
    setSelectedModule(mod);
    const nextOrder = (mod.subjects?.length || 0) + 1;
    setSubjectForm({ title: '', description: '', teacher_id: '', duration_weeks: '4', classes_per_week: '1', order: String(nextOrder) });
    setShowSubjectDialog(true);
  };

  const handleCreateSubject = async (e) => {
    e.preventDefault();
    if (!selectedModule) return;
    setError(''); setSubmitting(true);
    try {
      await createSubject(selectedModule.id, {
        title: subjectForm.title, description: subjectForm.description,
        teacher_id: subjectForm.teacher_id,
        duration_weeks: parseInt(subjectForm.duration_weeks),
        classes_per_week: parseInt(subjectForm.classes_per_week),
        order: parseInt(subjectForm.order)
      });
      toast.success('Curso agregado al módulo');
      setShowSubjectDialog(false);
      fetchData();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('¿Eliminar este curso?')) return;
    try { await deleteSubject(subjectId); toast.success('Curso eliminado'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openEditDialog = (sub) => {
    setEditSubject(sub);
    setEditForm({
      teacher_id: sub.teacher_id || '',
      duration_weeks: String(sub.duration_weeks),
      classes_per_week: String(sub.classes_per_week),
      order: String(sub.order)
    });
    setShowEditDialog(true);
  };

  const handleEditSubject = async (e) => {
    e.preventDefault();
    if (!editSubject) return;
    setSubmitting(true);
    try {
      await updateSubject(editSubject.id, {
        teacher_id: editForm.teacher_id,
        duration_weeks: parseInt(editForm.duration_weeks),
        classes_per_week: parseInt(editForm.classes_per_week),
        order: parseInt(editForm.order)
      });
      toast.success('Curso actualizado');
      setShowEditDialog(false);
      setEditSubject(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleUploadDoc = async (moduleId, file) => {
    setUploading(true);
    try {
      await uploadDocument(moduleId, file);
      toast.success(`"${file.name}" subido exitosamente`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (moduleId, docId, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    try { await deleteDocument(moduleId, docId); toast.success('Documento eliminado'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="admin-courses-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">Gestión de Módulos</h1>
            <p className="text-[#737373] mt-2">{modules.length} módulos — Cada módulo contiene cursos con docentes asignados</p>
          </div>
          <Button data-testid="admin-create-module-btn" onClick={() => setShowModuleDialog(true)} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuevo Módulo
          </Button>
        </div>

        {modules.length > 0 ? (
          <div className="space-y-6">
            {modules.map((mod) => (
              <Card key={mod.id} data-testid={`module-card-${mod.id}`} className="bg-white border border-[#E5DFD3] shadow-sm">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-xl text-[#1A1A1A]">{mod.title}</CardTitle>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#737373]">
                        <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {mod.enrolled_count} inscritos</span>
                        <span className="text-[#8B1538] font-medium">${mod.price_monthly?.toLocaleString()}/mes</span>
                        <span>${mod.price_annual?.toLocaleString()}/año</span>
                        <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" /> {mod.subjects_count} cursos</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${mod.is_active ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#737373]/10 text-[#737373]'}`}>
                        {mod.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteModule(mod.id)} className="text-[#9B2226] hover:bg-[#9B2226]/10"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#4A4A4A] mb-4 line-clamp-2">{mod.description}</p>
                  
                  {/* Subjects (Courses within module) */}
                  <div className="border border-[#E5DFD3] rounded-lg">
                    <div className="flex items-center justify-between px-4 py-3 bg-[#F5F2EA] rounded-t-lg border-b border-[#E5DFD3]">
                      <h3 className="text-sm font-semibold text-[#1A1A1A]">Cursos del Módulo</h3>
                      <Button data-testid={`add-subject-btn-${mod.id}`} size="sm" variant="outline" onClick={() => openSubjectDialog(mod)} className="border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538]/10 h-8 text-xs">
                        <Plus className="h-3 w-3 mr-1" /> Agregar Curso
                      </Button>
                    </div>
                    {mod.subjects?.length > 0 ? (
                      <div className="divide-y divide-[#E5DFD3]">
                        {mod.subjects.map((sub, idx) => (
                          <div key={sub.id} data-testid={`subject-row-${sub.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-[#FDFBF7]">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-[#8B1538] text-white flex items-center justify-center text-xs font-bold">{sub.order}</div>
                              <div>
                                <p className="font-medium text-sm text-[#1A1A1A]">{sub.title}</p>
                                <div className="flex items-center gap-3 text-xs text-[#737373]">
                                  <span>Docente: <span className="text-[#B8860B] font-medium">{sub.teacher_name}</span></span>
                                  <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" /> {sub.duration_weeks} sem.</span>
                                  <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" /> {sub.classes_per_week} clase/sem</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(sub)} className="text-[#B8860B] hover:bg-[#B8860B]/10 h-7" data-testid={`edit-subject-btn-${sub.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSubject(sub.id)} className="text-[#9B2226] hover:bg-[#9B2226]/10 h-7"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-sm text-[#737373]">Sin cursos. Agrega el primer curso a este módulo.</div>
                    )}
                  </div>

                  {/* Documents section */}
                  <div className="border border-[#E5DFD3] rounded-lg mt-4">
                    <div className="flex items-center justify-between px-4 py-3 bg-[#F5F2EA] rounded-t-lg border-b border-[#E5DFD3]">
                      <h3 className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-1.5"><FileText className="h-4 w-4 text-[#8B1538]" /> Documentos ({mod.documents?.length || 0})</h3>
                      <label className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md cursor-pointer border transition-colors ${uploading ? 'opacity-50 pointer-events-none' : 'border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538]/10'}`}>
                        <Upload className="h-3 w-3" /> {uploading ? 'Subiendo...' : 'Subir Archivo'}
                        <input type="file" className="hidden" disabled={uploading} data-testid={`upload-doc-input-${mod.id}`}
                          onChange={(e) => { if (e.target.files[0]) handleUploadDoc(mod.id, e.target.files[0]); e.target.value = ''; }}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.mp4,.mp3"
                        />
                      </label>
                    </div>
                    {mod.documents?.length > 0 ? (
                      <div className="divide-y divide-[#E5DFD3]">
                        {mod.documents.map((doc) => (
                          <div key={doc.id} data-testid={`doc-row-${doc.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-[#FDFBF7]">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-md bg-[#8B1538]/10 flex items-center justify-center"><FileText className="h-4 w-4 text-[#8B1538]" /></div>
                              <div>
                                <p className="font-medium text-sm text-[#1A1A1A]">{doc.filename}</p>
                                <p className="text-xs text-[#737373]">{(doc.size / 1024).toFixed(1)} KB — {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <a href={getDocumentUrl(doc.storage_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#2D6A4F] hover:bg-[#2D6A4F]/10"><Download className="h-3.5 w-3.5" /></a>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(mod.id, doc.id, doc.filename)} className="text-[#9B2226] hover:bg-[#9B2226]/10 h-7 w-7 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-sm text-[#737373]">Sin documentos. Sube archivos PDF, Word, etc.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <GraduationCap className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
            <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">No hay módulos</h3>
            <p className="text-[#737373]">Crea el primer módulo para comenzar</p>
          </div>
        )}

        {/* Create Module Dialog */}
        <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Crear Nuevo Módulo</DialogTitle>
              <DialogDescription>Define el módulo con su precio. Luego podrás agregar cursos dentro.</DialogDescription>
            </DialogHeader>
            {error && <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]"><AlertDescription>{error}</AlertDescription></Alert>}
            <form onSubmit={handleCreateModule} className="space-y-4">
              <div className="space-y-2">
                <Label>Título del Módulo</Label>
                <Input data-testid="module-title-input" value={moduleForm.title} onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})} required className="border-[#E5DFD3]" />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea data-testid="module-description-input" value={moduleForm.description} onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})} required rows={3} className="border-[#E5DFD3]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio Mensual ($)</Label>
                  <Input data-testid="module-price-monthly" type="number" value={moduleForm.price_monthly} onChange={(e) => setModuleForm({...moduleForm, price_monthly: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Precio Anual ($)</Label>
                  <Input data-testid="module-price-annual" type="number" value={moduleForm.price_annual} onChange={(e) => setModuleForm({...moduleForm, price_annual: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowModuleDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
                <Button type="submit" disabled={submitting} className="bg-[#8B1538] hover:bg-[#70102B] text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear Módulo'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Subject (Course) Dialog */}
        <Dialog open={showSubjectDialog} onOpenChange={setShowSubjectDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Agregar Curso al Módulo</DialogTitle>
              <DialogDescription>Agrega un curso a "{selectedModule?.title}"</DialogDescription>
            </DialogHeader>
            {error && <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]"><AlertDescription>{error}</AlertDescription></Alert>}
            <form onSubmit={handleCreateSubject} className="space-y-4">
              <div className="space-y-2">
                <Label>Título del Curso</Label>
                <Input data-testid="subject-title-input" value={subjectForm.title} onChange={(e) => setSubjectForm({...subjectForm, title: e.target.value})} required className="border-[#E5DFD3]" />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea value={subjectForm.description} onChange={(e) => setSubjectForm({...subjectForm, description: e.target.value})} rows={2} className="border-[#E5DFD3]" />
              </div>
              <div className="space-y-2">
                <Label>Docente Asignado</Label>
                {teachers.length > 0 ? (
                  <Select value={subjectForm.teacher_id} onValueChange={(v) => setSubjectForm({...subjectForm, teacher_id: v})}>
                    <SelectTrigger data-testid="subject-teacher-select" className="border-[#E5DFD3]"><SelectValue placeholder="Selecciona un docente" /></SelectTrigger>
                    <SelectContent>
                      {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-[#737373] italic p-2 bg-[#F5F2EA] rounded">No hay docentes registrados. Primero crea usuarios con rol profesor.</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Duración (semanas)</Label>
                  <Input data-testid="subject-duration-input" type="number" min="1" value={subjectForm.duration_weeks} onChange={(e) => setSubjectForm({...subjectForm, duration_weeks: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Clases/semana</Label>
                  <Input data-testid="subject-classes-input" type="number" min="1" value={subjectForm.classes_per_week} onChange={(e) => setSubjectForm({...subjectForm, classes_per_week: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Orden</Label>
                  <Input data-testid="subject-order-input" type="number" min="1" value={subjectForm.order} onChange={(e) => setSubjectForm({...subjectForm, order: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSubjectDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
                <Button type="submit" disabled={submitting || !subjectForm.teacher_id} className="bg-[#8B1538] hover:bg-[#70102B] text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Agregar Curso'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        {/* Edit Subject Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Editar Curso</DialogTitle>
              <DialogDescription>Modifica los datos de "{editSubject?.title}"</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubject} className="space-y-4">
              <div className="space-y-2">
                <Label>Docente Asignado</Label>
                {teachers.length > 0 ? (
                  <Select value={editForm.teacher_id} onValueChange={(v) => setEditForm({...editForm, teacher_id: v})}>
                    <SelectTrigger data-testid="edit-teacher-select" className="border-[#E5DFD3]"><SelectValue placeholder="Selecciona un docente" /></SelectTrigger>
                    <SelectContent>
                      {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-[#737373] italic p-2 bg-[#F5F2EA] rounded">No hay docentes registrados.</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Duración (semanas)</Label>
                  <Input type="number" min="1" value={editForm.duration_weeks} onChange={(e) => setEditForm({...editForm, duration_weeks: e.target.value})} className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Clases/semana</Label>
                  <Input type="number" min="1" value={editForm.classes_per_week} onChange={(e) => setEditForm({...editForm, classes_per_week: e.target.value})} className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Orden</Label>
                  <Input type="number" min="1" value={editForm.order} onChange={(e) => setEditForm({...editForm, order: e.target.value})} className="border-[#E5DFD3]" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
                <Button type="submit" disabled={submitting || !editForm.teacher_id} className="bg-[#8B1538] hover:bg-[#70102B] text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar Cambios'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
